import React from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
    Dialog,
    DialogContent,
    DialogActions,
    Button,
    Box,
    Typography,
    alpha,
    useTheme,
    IconButton,
    useMediaQuery
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { brokerConfig } from '../../config/brokerConfig';
import { setUserSettingsAction, updateUserSettingsAndRefreshAction } from '../../store/actions/profile';
import { updateUserSettings } from '../../services/backendAPI';

const FeedbackModal = ({ open, onClose, onContinue }) => {
    const theme = useTheme();
    const dispatch = useDispatch();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const userData = useSelector(state => state.profile.userData);
    const userSettings = useSelector(state => state.profile.userSettings);

    // Get the configured feedback URL
    const feedbackUrl = brokerConfig.feedback?.url;

    const handleYesClick = async () => {
        // Use the pattern requested by the user for updating settings
        if (!userSettings?.feedback_provided) {
            const settingsUpdatePayload = {
                feedback_provided: true,
            };

            try {
                dispatch({ type: 'UPDATE_USER_SETTINGS_START' });
                await updateUserSettings(settingsUpdatePayload);

                dispatch(setUserSettingsAction({
                    ...userSettings,
                    ...settingsUpdatePayload
                }));
            } catch (error) {
                console.error("Failed to update feedback settings", error);
                dispatch({ type: 'UPDATE_USER_SETTINGS_FAILURE', payload: error.message || 'Failed to update settings' });
            }
        }

        if (feedbackUrl) {
            window.open(feedbackUrl, '_blank');
        }
        onClose(); // Close the modal after redirect
        if (onContinue) onContinue(true);
    };

    const handleNotNowClick = () => {
        onClose();
        if (onContinue) onContinue(false);
    };

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="xs"
            fullWidth
            fullScreen={isMobile}
            PaperProps={{
                sx: {
                    borderRadius: isMobile ? 0 : 3,
                    boxShadow: '0 8px 32px rgba(0,0,0,0.1)',
                    overflow: 'hidden',
                    maxWidth: isMobile ? '100%' : '400px !important',
                    height: isMobile ? '100%' : 'auto',
                    display: 'flex',
                    flexDirection: 'column'
                }
            }}
        >
            <Box sx={{
                background: `linear-gradient(120deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
                p: isMobile ? 2 : 2.5,
                position: 'relative',
                overflow: 'hidden'
            }}>
                <Box sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    opacity: 0.1,
                    backgroundImage: 'radial-gradient(circle at 2px 2px, white 1px, transparent 0)',
                    backgroundSize: '24px 24px',
                }} />
                <Box sx={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    justifyContent: 'space-between',
                    pt: isMobile ? 'env(safe-area-inset-top)' : 0
                }}>
                    <Box>
                        <Typography variant="h6" sx={{
                            color: 'white',
                            fontWeight: 700,
                            mb: 0.5,
                            fontSize: isMobile ? '1.25rem' : '1.125rem'
                        }}>
                            Enjoying our app?
                        </Typography>
                        <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.8)' }}>
                            We value your feedback
                        </Typography>
                    </Box>
                    <IconButton
                        onClick={onClose}
                        size="small"
                        sx={{
                            color: 'white',
                            bgcolor: 'rgba(255,255,255,0.1)',
                            '&:hover': { bgcolor: 'rgba(255,255,255,0.2)' }
                        }}
                    >
                        <CloseIcon fontSize="small" />
                    </IconButton>
                </Box>
            </Box>

            <DialogContent sx={{
                p: 2.5,
                flex: isMobile ? 1 : 'initial',
                overflow: 'auto',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center'
            }}>
                <Box sx={{
                    mb: 3,
                    p: 3,
                    textAlign: 'center',
                    bgcolor: theme.palette.mode === 'dark'
                        ? alpha(theme.palette.primary.main, 0.05)
                        : alpha(theme.palette.primary.light, 0.05),
                    borderRadius: 2,
                    border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
                    width: '100%'
                }}>
                    <Typography variant="body1" sx={{ fontWeight: 500, color: theme.palette.text.primary }}>
                        Would you like to provide feedback?
                    </Typography>
                </Box>
            </DialogContent>

            <DialogActions sx={{
                p: 2.5,
                bgcolor: theme.palette.mode === 'dark'
                    ? alpha(theme.palette.background.paper, 0.5)
                    : alpha(theme.palette.grey[50], 0.5),
                borderTop: `1px solid ${theme.palette.divider}`,
                pb: isMobile ? 'calc(env(safe-area-inset-bottom) + 20px)' : 2.5
            }}>
                <Box sx={{
                    width: '100%',
                    display: 'flex',
                    flexDirection: isMobile ? 'column' : 'row',
                    gap: isMobile ? 1.5 : 1,
                    justifyContent: 'flex-end'
                }}>
                    <Button
                        onClick={handleNotNowClick}
                        size={isMobile ? "large" : "small"}
                        fullWidth={isMobile}
                        variant={isMobile ? "outlined" : "text"}
                        sx={{
                            color: theme.palette.text.secondary,
                            order: isMobile ? 2 : 1
                        }}
                    >
                        Not now
                    </Button>
                    <Button
                        variant="contained"
                        onClick={handleYesClick}
                        endIcon={<ArrowForwardIcon />}
                        size={isMobile ? "large" : "small"}
                        fullWidth={isMobile}
                        sx={{
                            borderRadius: 1.5,
                            px: 2,
                            bgcolor: theme.palette.success.main,
                            '&:hover': {
                                bgcolor: theme.palette.success.dark
                            },
                            order: isMobile ? 1 : 2
                        }}
                    >
                        Yes
                    </Button>
                </Box>
            </DialogActions>
        </Dialog>
    );
};

export default FeedbackModal;
